/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2023 (original work) Open Assessment Technologies SA ;
 */
import pluginFactory from 'taoTests/runner/plugin';
import { getItemProperty } from '../../../util/testMap.js';

// Identifier for the plugin
const pluginName = 'areaHider';

const categoryToAreaIdPrefix = 'x-tao-option-areaHider-';

/**
 * @typedef HiddenAreaDescriptor
 * @property {string} id id of area in areaBroker
 * @property {HTMLElement} areaElementRef reference to the layout element
 * @property {string} initialDisplayValue display property initial value of a hidden element
 */

/**
 * Restores the initial value of display property for hidden areas
 * @param {HiddenAreaDescriptor[]} hiddenAreasArray array
 */
function restoreInitialDisplayValue(hiddenAreasArray) {
    hiddenAreasArray.forEach(hiddenAreaDescriptor => {
        hiddenAreaDescriptor.areaElementRef.style.display = hiddenAreaDescriptor.initialDisplayValue;
    });
}

/**
 * Merges plugin config and provided item categories to the array of area ids to hide
 * @param {String[]} categories categories of the item
 * @returns {String[]} array of area ids to hide
 */
function getHiddenAreaIds(categories) {
    let hiddenAreas = [];
    if (categories) {
        categories.forEach(category => {
            const areaId = category.replace(categoryToAreaIdPrefix, '');
            hiddenAreas.push(areaId);
        });
    }

    return hiddenAreas;
}

/**
 * the areaHider plugin allows to hide areas of testRunner layout defined in areaBroker
 */
export default pluginFactory({
    name: pluginName,

    /**
     * Prepares the plugin, adding API
     */
    install() {
        const testRunner = this.getTestRunner();
        const pluginConfig = testRunner.getPluginConfig(this.getName());
        this.hiddenAreas = pluginConfig && pluginConfig.hiddenAreas;

        //array of areas descriptors disabled by item categories
        this.itemHiddenAreas = [];
    },

    /**
     * Starts the behavior, listening to events that will activate the plugin
     */
    init() {
        const testRunner = this.getTestRunner();
        /**
         * When loading item, hide the required areas
         */
        testRunner
            .on(`loaditem.${pluginName}`, () => {
                const { testPartId, sectionId, itemIdentifier } = testRunner.getTestContext();
                const testMap = testRunner.getTestMap();
                const categories = getItemProperty(testMap, testPartId, sectionId, itemIdentifier, 'categories');
                const areaBroker = this.getAreaBroker();
                const hiddenAreaIds = getHiddenAreaIds(categories);

                //set css display value of area to 'none' and store its initial state
                hiddenAreaIds.forEach(areaId => {
                    const areaElementRef = areaBroker.getArea(areaId);
                    if (areaElementRef) {
                        const initialDisplayValue = window.getComputedStyle(areaElementRef).display;
                        areaElementRef.style.display = 'none';
                        this.itemHiddenAreas.push({ id: areaId, areaElementRef, initialDisplayValue });
                    }
                });
            })
            /**
             * When leaving item, make sure the tool is closed
             */
            .on(`unloaditem.${pluginName}`, () => {
                restoreInitialDisplayValue(this.itemHiddenAreas);
                //prevent memory leak
                this.itemHiddenAreas = [];
            });
    },

    /**
     * Destroy the plugin and its components. Normally called only at the end of a test session.
     */
    destroy() {
        restoreInitialDisplayValue(this.itemHiddenAreas);
        this.getTestRunner().off(`.${pluginName}`);
        //prevent memory leak
        this.itemHiddenAreas = [];
    }
});
